import { getAttachmentValues } from '@qdabra/infopath-attachments';
import { includes } from 'ramda';
import {
    paramAttr,
    paramDsName,
    paramXPath,
} from '../constants/parameterNames';
import {
    typeDataSource,
    typeOption,
    typeXPath,
} from '../constants/parameterTypes';
import { failure } from '../helpers/commandResult';
import { getParamValue } from '../helpers/parameter.accessors';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';
import { getValidNode } from '../helpers/qRules.node.helpers';

const ATTR_NAME = 'name';
const ATTR_EXT = 'ext';
const ATTR_SIZE = 'size';
const ATTR_FILE = 'file';

const optionalParameters = [
    {
        name: paramDsName,
        description: 'Data Source Name',
        type: typeDataSource,
    },
];

const requiredParameters = [
    {
        name: paramXPath,
        description: 'XPath to field to attachment',
        type: typeXPath,
        dsParentName: paramDsName,
    },
    {
        name: paramAttr,
        description:
            'Specific attribute value to get (Name | Ext | Size | File)',
        type: typeOption,
        options: [
            {
                name: 'Name',
                value: ATTR_NAME,
            },
            {
                name: 'Ext',
                value: ATTR_EXT,
            },
            {
                name: 'Size',
                value: ATTR_SIZE,
            },
            {
                name: 'File',
                value: ATTR_FILE,
            },
        ],
    },
];

const ERROR_INVALID_ATTRIBUTE_NAME = 'An invalid attribute was specified.';
const errorFailedToSelectAttachment = 'Failed to select the attachment node.';
const errorBlankAttachment = 'The attachment node is blank.';
const description =
    'Allows you the ability to obtain attributes from an attachment included in an InfoPath form in a File Control.';

const VALID_ATTRIBUTES = [ATTR_NAME, ATTR_EXT, ATTR_SIZE, ATTR_FILE];

// String -> Boolean
const validateParameterValues = (attr) => includes(attr, VALID_ATTRIBUTES);

function getAttributeValue(attachmentValue, attrName) {
    const properties = getAttachmentValues(attachmentValue);

    switch (attrName) {
        case ATTR_NAME:
            return properties.fileName;

        case ATTR_EXT:
            return properties.fileExtensionWithoutPeriod;

        case ATTR_SIZE:
            return properties.fileSize;

        case ATTR_FILE:
            return properties.base64;

        default:
            throw new Error(ERROR_INVALID_ATTRIBUTE_NAME);
    }
}

function execute({ dataSources, parameters }) {
    const dsName = getParamValue(parameters, paramDsName);
    const xpath = getParamValue(parameters, paramXPath);
    const attr = getParamValue(parameters, paramAttr).toLowerCase();

    if (!validateParameterValues(attr)) {
        return failure(ERROR_INVALID_ATTRIBUTE_NAME);
    }

    const dataSource = getDataSource(dataSources, dsName);
    const node = getValidNode(dataSource, xpath, errorFailedToSelectAttachment);

    const nodeValue = node.value();

    if (!nodeValue) {
        return failure(errorBlankAttachment);
    }

    return getAttributeValue(nodeValue, attr);
}

const GetAttachmentAttribute = {
    description,
    optionalParameters,
    requiredParameters,
    execute,
};

export default GetAttachmentAttribute;
